import { useRef, type ReactNode } from "react";
import type { CatalogCard } from "../api/client";
import type { Theme } from "../theme/pixel";
import { Card, naturalCardWidthPx } from "./Card";
import { catalogToCardView } from "../logic/catalogToCardView";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { useModalFocus } from "../logic/useModalFocus";

// Full-screen card detail modal. Click backdrop or press Escape to close.
export function Inspector({
  card,
  theme,
  onClose,
  addAction,
  secondaryActions,
}: {
  card: CatalogCard | null;
  theme: Theme;
  onClose: () => void;
  addAction?: {
    canAdd: boolean;
    reason?: string;
    onAdd: () => void;
  };
  /** Stacked below the Add to Deck button. Used by the NPC deck editor
   *  to surface a Regen Image button without leaking admin concerns
   *  into the regular deck builder Inspector. */
  secondaryActions?: ReactNode;
}) {
  if (!card) return <InspectorEmpty />;
  return (
    <InspectorBody
      card={card}
      theme={theme}
      onClose={onClose}
      addAction={addAction}
      secondaryActions={secondaryActions}
    />
  );
}

function InspectorEmpty() {
  return null;
}

function InspectorBody({
  card,
  theme,
  onClose,
  addAction,
  secondaryActions,
}: {
  card: CatalogCard;
  theme: Theme;
  onClose: () => void;
  addAction?: {
    canAdd: boolean;
    reason?: string;
    onAdd: () => void;
  };
  secondaryActions?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${card.name} details`}
      tabIndex={-1}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10, 8, 4, 0.82)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        padding: 40,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: naturalCardWidthPx("hero") }}>
        <Card card={catalogToCardView(card)} size="hero" theme={theme} keywordTooltips />
      </div>

      {(addAction || secondaryActions) && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          {addAction && (
            <button
              onClick={() => {
                if (!addAction.canAdd) return;
                addAction.onAdd();
                onClose();
              }}
              disabled={!addAction.canAdd}
              title={addAction.canAdd ? undefined : addAction.reason}
              {...withBtnStyle(primaryBtn(theme, !addAction.canAdd), {
                fontSize: 11,
                padding: "8px 16px",
              })}
            >
              + ADD TO DECK
            </button>
          )}
          {secondaryActions}
        </div>
      )}
    </div>
  );
}
